// Hero component for the Raidu landing page
// Displays the main headline, industry cards, and AI platform icons
import IndustryCards from './IndustryCards';

/**
 * Hero component for the landing page
 * Displays the main headline, subheadline, CTAs, and visual elements
 * 
 * @returns {JSX.Element} The rendered Hero component
 */
export default function Hero() {
  try {
    // Log component rendering
    console.log('Rendering Hero component');

    // No need for industry data here as it's moved to IndustryCards component

    // AI platform icons for the bottom row - no duplicates
    const platformIcons = [
      // Core LLM providers (OpenAI excluded as it appears in industry cards)
      'anthropic.svg',       // Anthropic (Claude)
      'mistral-color.svg',   // Mistral AI
      'cohere-color.svg',    // Cohere
      'groq.svg',            // Groq
      'meta-color.svg',      // Meta's Llama
      
      // Cloud AI platforms
      'azureai-color.svg',   // Azure AI
      'gemini-color.svg',    // Google Gemini
      
      // Open source & infrastructure
      'ollama.svg',          // Ollama
      'deepseek-color.svg',  // DeepSeek
      
      // Image generation (Hugging Face moved to avoid duplicate)
      'midjourney.svg',      // Midjourney
    ];

    return (
      <div className="relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
        </div>

        {/* Main content container with increased margins for premium enterprise feel */}
        <div className="container mx-auto px-6 py-8 md:py-12 md:px-8 lg:px-12 xl:px-16 flex flex-col md:flex-row gap-8 lg:gap-12 items-start max-w-7xl relative z-10">
          {/* Left column - Text content */}
          <div className="w-full md:w-1/2 pt-6 md:pt-12">
            {/* Heading with refined typography */}
            <div className="mb-12">
              <h1 className="text-6xl md:text-7xl font-bold mb-4 tracking-tight leading-none">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-indigo-600 block">AI Adoption.</span>
              </h1>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600">Simplified. </span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-pink-600">Secure. </span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-indigo-600">Compliant.</span>
              </h2>
            </div>
            
            {/* Description with enhanced typography */}
            <p className="text-gray-700 text-xl mb-8 max-w-lg leading-relaxed font-light">
              Raidu enables enterprises to roll out AI across teams with built-in security, data governance, and compliance controls from day one.
            </p>
            
            {/* Optional Tagline with refined styling */}
            <p className="text-gray-600 text-lg mb-12 max-w-lg leading-relaxed font-light border-l-4 border-indigo-300 pl-4 py-1">
              Adopt AI with speed without sacrificing trust, control, or compliance.
            </p>
            
            {/* CTA buttons */}
            <div className="flex flex-wrap gap-5">
              <a
                href="/booking"
                className="rounded-md bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-indigo-600 transition-colors"
                onClick={() => {
                  try {
                    // Log primary CTA button click
                    console.log('Book a Demo button clicked');
                  } catch (error) {
                    // Log any errors during click handling
                    console.error('Error handling Book a Demo button click:', error);
                  }
                }}
              >
                Book a Demo
              </a>
              <a
                href="/booking"
                className="rounded-md bg-white border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                onClick={() => {
                  try {
                    // Log secondary CTA button click
                    console.log('Download Security Brief button clicked');
                  } catch (error) {
                    // Log any errors during click handling
                    console.error('Error handling Download Security Brief button click:', error);
                  }
                }}
              >
                Download Security Brief
              </a>
            </div>
            

          </div>
          
          {/* Right column - Industry cards */}
          <div className="w-full md:w-1/2 pt-6 md:pt-12">
            <IndustryCards />
          </div>
        </div>
        
        {/* AI platform icons section at the bottom */}
        <div className="container mx-auto px-6 pt-16 pb-16 relative z-10">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-8">
            {platformIcons.map((icon, index) => (
              <img 
                key={index} 
                src={`/ai-icons/${icon}`} 
                alt={`AI Platform ${index + 1}`} 
                className="h-8 w-auto object-contain opacity-90 hover:opacity-100 hover:scale-110 transition-all duration-300"
                onError={(e) => {
                  try {
                    // Fallback to OpenAI icon if image fails to load
                    console.error(`Error loading icon: ${icon}`);
                    e.currentTarget.src = "/ai-icons/openai.svg";
                  } catch (error) {
                    // Log any errors during error handling
                    console.error('Error in fallback image handling:', error);
                  }
                }}
              />
            ))}
            <span className="text-sm text-gray-500 font-medium">+many more</span>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors that occur during rendering
    console.error('Error rendering Hero component:', error);
    
    // Fallback UI in case of error
    return (
      <div className="bg-white py-24 text-center">
        <p className="text-gray-600">An error occurred while loading the hero section. Please try refreshing the page.</p>
      </div>
    );
  }
}
